import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export type AgingFaixa = 
  | '0_a_vencer' 
  | '1_ate_30' 
  | '2_ate_60' 
  | '3_ate_90' 
  | '4_acima_90';

interface AgingBadgeProps {
  faixa: AgingFaixa | string;
  className?: string;
  showLabel?: boolean;
}

const AGING_CONFIG: Record<AgingFaixa, { label: string; labelKey: string; className: string }> = {
  '0_a_vencer': {
    label: 'A Vencer',
    labelKey: 'finance.aging.due',
    className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  '1_ate_30': {
    label: '1-30 dias',
    labelKey: 'finance.aging.1to30',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  },
  '2_ate_60': {
    label: '31-60 dias',
    labelKey: 'finance.aging.31to60',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  },
  '3_ate_90': {
    label: '61-90 dias',
    labelKey: 'finance.aging.61to90',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  '4_acima_90': {
    label: '90+ dias',
    labelKey: 'finance.aging.90plus',
    className: 'bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-300',
  },
};

export const AGING_COLORS = {
  '0_a_vencer': '#48BB78',  // Verde
  '1_ate_30': '#ECC94B',    // Amarelo
  '2_ate_60': '#ED8936',    // Laranja
  '3_ate_90': '#FC8181',    // Vermelho claro
  '4_acima_90': '#E53E3E',  // Vermelho escuro
};

export function AgingBadge({ faixa, className, showLabel = true }: AgingBadgeProps) {
  const { t } = useTranslation();
  const config = AGING_CONFIG[faixa as AgingFaixa] || AGING_CONFIG['0_a_vencer'];
  
  const label = t(config.labelKey, { defaultValue: config.label });

  return (
    <span
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        config.className,
        className
      )}
    >
      {showLabel ? label : null}
    </span>
  );
}

/**
 * Get aging faixa from days overdue
 */
export function getAgingFaixa(diasAtraso: number): AgingFaixa {
  if (diasAtraso <= 0) return '0_a_vencer';
  if (diasAtraso <= 30) return '1_ate_30';
  if (diasAtraso <= 60) return '2_ate_60';
  if (diasAtraso <= 90) return '3_ate_90';
  return '4_acima_90';
}
